import { showChannelRequestErrorToast, showErrorToastAndLog } from 'services/toaster';
import { isChannelRequestTimeout } from 'shared/channels/errors';
import { Identificable } from 'shared/types/commonView';

export function findModelForEditing<T extends Identificable>(
  models: T[],
  currentId: string | undefined
): T | undefined {
  return models.find((comp) => comp._id === currentId);
}

interface SubmitServerChangeParams<TResult> {
  /** The change sent through the worker; resolves once the server accepted it. */
  change: () => Promise<TResult>;
  setOpen: (open: boolean) => void;
  setIsPosting: (isPosting: boolean) => void;
  /** Logged with the generic toast, e.g. `creating a site`. */
  action: string;
}

export type ServerChangeOutcome<TResult> = { saved: true; result: TResult } | { saved: false };

/**
 * Submits a form's change and resolves whether the server accepted it, with
 * the change's result (e.g. the created entity). A timeout closes the form
 * (the change may still be saved); a server error keeps it open and shows the
 * server's message when it sent one.
 */
export async function submitServerChange<TResult>({
  change,
  setOpen,
  setIsPosting,
  action,
}: SubmitServerChangeParams<TResult>): Promise<ServerChangeOutcome<TResult>> {
  setIsPosting(true);
  try {
    const result = await change();
    setOpen(false);
    return { saved: true, result };
  } catch (error) {
    if (isChannelRequestTimeout(error)) {
      setOpen(false);
      // The timeout itself is already reported with diagnostics by the channel layer.
      showErrorToastAndLog(`Timeout while ${action}`);
      return { saved: false };
    }
    showChannelRequestErrorToast(`Error while ${action}`, error);
    return { saved: false };
  } finally {
    setIsPosting(false);
  }
}
